import { useState, useEffect, useMemo, useCallback } from 'react'
import { switchClaudeConsumer } from '../lib/api'
import { loadMainViewOverride, persistMainView, resolveMainView, type MainView } from '../lib/mainView'

// Shared "which view owns the main pane" state + switch logic, used by both
// WorkspaceLayout (desktop) and MobileWorkspaceLayout (mobile). The override
// is scoped per (app, WORK AREA): work areas are independent worlds, so
// switching one to the terminal must not drag the others along — changing
// the work-area dropdown simply re-reads that area's remembered view, with
// no consumer handoff (those fire only on explicit view toggles below).
// Settings load asynchronously, so when no override exists yet the effective
// view falls back to the `claude_panel_enabled` setting's default and adopts
// it once it arrives.
export function useMainView(
  appName: string | undefined,
  activeSession: string | undefined,
  claudePanelEnabled: boolean,
  onAfterSwitch?: (next: MainView) => void,
): { mainView: MainView; switchView: (next: MainView) => Promise<void> } {
  const workArea = activeSession || 'main'
  const [viewOverride, setViewOverride] = useState<MainView | null>(() => loadMainViewOverride(appName || '', workArea))
  useEffect(() => {
    setViewOverride(loadMainViewOverride(appName || '', workArea))
  }, [appName, workArea])
  const mainView = useMemo(
    () => resolveMainView(viewOverride, claudePanelEnabled),
    [viewOverride, claudePanelEnabled]
  )

  // Switches the main pane between the Claude panel and the terminal, and
  // notifies the workspace container so exactly one Claude CLI consumer runs.
  //
  // Ordering matters for terminal: the handoff arms the deferred-start marker
  // that the terminal attach consumes — mounting the terminal first would
  // race the marker (attach finds nothing, marker armed a beat later and sits
  // stale). So AWAIT the call before mounting the terminal; the panel
  // direction reclaims lazily and can stay fire-and-forget.
  const switchView = useCallback(async (next: MainView) => {
    persistMainView(appName || '', workArea, next)
    if (next === 'terminal' && appName) {
      try {
        await switchClaudeConsumer(appName, 'terminal', activeSession)
      } catch (e) {
        console.warn('[workspace] consumer switch failed', e)
      }
    } else if (appName) {
      switchClaudeConsumer(appName, 'panel', activeSession)
        .catch(e => console.warn('[workspace] consumer switch failed', e))
    }
    setViewOverride(next)
    onAfterSwitch?.(next)
  }, [appName, activeSession, workArea, onAfterSwitch])

  return { mainView, switchView }
}
